import axios from "axios";
import { Edit, LoaderCircle, Search, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import CreateUserModal from "../components/CreateUserModal ";
import { permissions } from "../helper/permissions";
import { toast } from "react-toastify";

const LIMIT = 10;

const Projects = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const user = useAuth().user;
  const rolePerms = permissions[user.role];

  const { data = { users: [], total: 0 }, isLoading: loading } = useQuery({
    queryKey: ["users", { page, search }],
    queryFn: async () => {
      const skip = (page - 1) * LIMIT;
      const url = search
        ? `https://dummyjson.com/users/search?q=${search}&limit=${LIMIT}&skip=${skip}`
        : `https://dummyjson.com/users?limit=${LIMIT}&skip=${skip}`;

      const res = await axios.get(url);
      return res.data;
    },
  });

  const users = data.users || [];
  const total = data.total || 0;

  const handleCreate = () => {
    toast.success("User created successfully (UI only)");
    setOpenModal(false);
  };

  const handleEdit = () => {
    toast.success("Access Denied (Create UI only)");
  };

  const handleDelete = () => {
    toast.error("User deleted (UI only)");
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="bg-white rounded-xl p-5 shadow-md">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold">Projects</h2>

        <div className=" relative flex gap-2 ">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            placeholder="Search users..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="border px-3 py-2 rounded text-sm pl-8 w-auto"
          />

          {rolePerms.canCreate && (
            <button
              onClick={() => setOpenModal(true)}
              className="bg-black text-white px-4 py-2 rounded text-sm"
            >
              + Create
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Age</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="p-4 text-center">
                  <LoaderCircle className="h-6 w-6 mx-auto animate-spin" />
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    {u.firstName} {u.lastName}
                  </td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3">{u.age}</td>
                  <td className="p-3 flex gap-2">
                    {rolePerms.canEdit ? (
                      <button onClick={handleEdit} className="text-sm">
                        <Edit className="h-4 w-4" />
                      </button>
                    ) : (
                      "-"
                    )}
                    {rolePerms.canDelete && (
                      <button onClick={handleDelete} className=" text-sm">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between mt-4">
        <span className="text-sm text-gray-500">
          Page {page} of {totalPages}
        </span>

        <div className="flex gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setPage(page + 1)}
            className="px-3 py-1 border rounded"
          >
            Next
          </button>
        </div>
      </div>

      {/* Modal */}
      <CreateUserModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onCreate={handleCreate}
      />
    </div>
  );
};

export default Projects;
